import { FormikErrors } from 'formik'
import { IEmailCollectionItem, isValidEmail } from '@sales-center/common'
import { ILeadFormModel } from '@user/components/Leads/AddLeadPage/typings'
import { AppDispatch } from '@user/store/hooks/useAppDispatch'
import { validateMultiEmail } from './api'
import { EmailValidationVerdict, IEmailValidationModel } from './typings'

export const INVALID_DOMAIN_MESSAGE = 'Must be a valid domain'
export const EMAIL_VALIDATION_PREFIX = 'emailValidation:'

export function parseEmailValidationErrorMessages(
  message?: string | null,
): string[] {
  if (!message?.includes(EMAIL_VALIDATION_PREFIX)) {
    return []
  }

  const prefixIndex = message.indexOf(EMAIL_VALIDATION_PREFIX)
  const rawMessage = message
    .slice(prefixIndex + EMAIL_VALIDATION_PREFIX.length)
    .trim()

  const seen = new Set<string>()
  const uniqueMessages: string[] = []

  for (const line of rawMessage.split(/\r?\n/)) {
    const trimmed = line.trim()
    if (!trimmed) {
      continue
    }

    const dedupeKey = trimmed.toLowerCase()
    if (seen.has(dedupeKey)) {
      continue
    }

    seen.add(dedupeKey)
    uniqueMessages.push(trimmed)
  }

  return uniqueMessages
}

/**
 * Validates one or more email addresses via the marketing API (core PestPac rules).
 * Returns validation results in the same order as the input emails.
 */
export async function validateEmails(
  dispatch: AppDispatch,
  emails: string[],
): Promise<IEmailValidationModel[]> {
  if (!emails?.length) {
    return []
  }

  const result = await dispatch(validateMultiEmail(emails))

  if (validateMultiEmail.fulfilled.match(result)) {
    return result.payload
  }

  throw result.payload
}

function collectEmailsFromCollection(emails: IEmailCollectionItem[]): string[] {
  const seen = new Set<string>()
  const collected: string[] = []

  for (const item of emails) {
    const trimmed = item?.emailAddress?.trim() ?? ''
    if (!trimmed || !isValidEmail(trimmed) || seen.has(trimmed)) {
      continue
    }
    seen.add(trimmed)
    collected.push(trimmed)
  }

  return collected
}

/** Collects deduplicated, format-valid emails from add-lead location fields. */
export function collectAddLeadEmails(model: ILeadFormModel): string[] {
  const emails = collectEmailsFromCollection(model.emailAddresses)

  if (!model.locationSameAsBillTo) {
    emails.push(...collectEmailsFromCollection(model.billTo_emailAddresses))
  }

  return emails
}

function isInvalidEmailResult(result: IEmailValidationModel): boolean {
  return (
    result.verdict === EmailValidationVerdict.Invalid ||
    (result.errors?.length ?? 0) > 0
  )
}

/** Extracts invalid email addresses from backend emailValidation: error messages. */
export function extractInvalidEmailsFromValidationMessages(
  messages: string[],
): Set<string> {
  const invalidEmails = new Set<string>()

  for (const message of messages) {
    for (const match of message.matchAll(/([^\s,]+@[^\s,]+)\s*:/g)) {
      invalidEmails.add(match[1].toLowerCase())
    }
  }

  return invalidEmails
}

export function applyInvalidDomainErrorsToEmailCollection(
  emails: IEmailCollectionItem[],
  invalidEmails: Set<string> | string[],
): FormikErrors<IEmailCollectionItem>[] | undefined {
  const invalidSet =
    invalidEmails instanceof Set
      ? invalidEmails
      : new Set(invalidEmails.map((email) => email.toLowerCase()))

  return applyInvalidDomainToCollection(emails, invalidSet)
}

function applyInvalidDomainToCollection(
  emails: IEmailCollectionItem[],
  invalidEmails: Set<string>,
): FormikErrors<IEmailCollectionItem>[] | undefined {
  if (invalidEmails.size === 0) {
    return undefined
  }

  const emailErrors: FormikErrors<IEmailCollectionItem>[] = emails.map(
    (item) => {
      const trimmed = item?.emailAddress?.trim() ?? ''
      if (trimmed && invalidEmails.has(trimmed.toLowerCase())) {
        return { emailAddress: INVALID_DOMAIN_MESSAGE, isPrimary: '' }
      }
      return { emailAddress: '', isPrimary: '' }
    },
  )

  if (emailErrors.findIndex((x) => x.emailAddress !== '') >= 0) {
    return emailErrors
  }

  return undefined
}

/** Maps API validation results to Formik errors on email collection fields. */
export function applyInvalidDomainErrors(
  model: ILeadFormModel,
  results: IEmailValidationModel[],
): FormikErrors<ILeadFormModel> {
  const invalidEmails = new Set(
    results
      .filter(isInvalidEmailResult)
      .map((r) => r.email.trim().toLowerCase()),
  )

  const errors: FormikErrors<ILeadFormModel> = {}

  const serviceErrors = applyInvalidDomainToCollection(
    model.emailAddresses,
    invalidEmails,
  )
  if (serviceErrors) {
    errors.emailAddresses = serviceErrors
  }

  if (!model.locationSameAsBillTo) {
    const billToErrors = applyInvalidDomainToCollection(
      model.billTo_emailAddresses,
      invalidEmails,
    )
    if (billToErrors) {
      errors.billTo_emailAddresses = billToErrors
    }
  }

  return errors
}

function mergeEmailCollectionErrors(
  sync?: FormikErrors<IEmailCollectionItem>[],
  domain?: FormikErrors<IEmailCollectionItem>[],
): FormikErrors<IEmailCollectionItem>[] | undefined {
  const length = Math.max(sync?.length ?? 0, domain?.length ?? 0)
  if (length === 0) {
    return undefined
  }

  const combined: FormikErrors<IEmailCollectionItem>[] = []
  for (let i = 0; i < length; i++) {
    const syncMessage = sync?.[i]?.emailAddress ?? ''
    const domainMessage = domain?.[i]?.emailAddress ?? ''
    combined.push({
      emailAddress: domainMessage || syncMessage,
      isPrimary: '',
    })
  }

  return combined.some((entry) => entry.emailAddress) ? combined : undefined
}

/** Merges sync Formik errors with async domain validation errors (same shape as validateLocation). */
export function mergeLeadFormErrors(
  syncErrors: FormikErrors<ILeadFormModel>,
  domainErrors: FormikErrors<ILeadFormModel>,
): FormikErrors<ILeadFormModel> {
  const merged: FormikErrors<ILeadFormModel> = {
    ...syncErrors,
    ...domainErrors,
  }

  const serviceErrors = mergeEmailCollectionErrors(
    syncErrors.emailAddresses as
      | FormikErrors<IEmailCollectionItem>[]
      | undefined,
    domainErrors.emailAddresses as
      | FormikErrors<IEmailCollectionItem>[]
      | undefined,
  )
  if (serviceErrors) {
    merged.emailAddresses = serviceErrors
  } else if (!serviceErrors && merged.emailAddresses) {
    delete merged.emailAddresses
  }

  const billToErrors = mergeEmailCollectionErrors(
    syncErrors.billTo_emailAddresses as
      | FormikErrors<IEmailCollectionItem>[]
      | undefined,
    domainErrors.billTo_emailAddresses as
      | FormikErrors<IEmailCollectionItem>[]
      | undefined,
  )
  if (billToErrors) {
    merged.billTo_emailAddresses = billToErrors
  } else if (!billToErrors && merged.billTo_emailAddresses) {
    delete merged.billTo_emailAddresses
  }

  return merged
}

/**
 * Validates email domains for add-lead when creating a new PestPac location.
 * Returns Formik errors to merge, or null when valid / no emails to check.
 */
export async function validateAddLeadEmailDomains(
  dispatch: AppDispatch,
  model: ILeadFormModel,
): Promise<FormikErrors<ILeadFormModel> | null> {
  const emails = collectAddLeadEmails(model)
  if (!emails.length) {
    return null
  }

  const results = await validateEmails(dispatch, emails)
  const domainErrors = applyInvalidDomainErrors(model, results)

  return Object.keys(domainErrors).length > 0 ? domainErrors : null
}
